<template>
  <ValidationObserver
      ref="observer"
      class="transition duration-500 ease-in-out transform w-full"
      style="transition: all 1s !important"
      tag="form"
      @submit.prevent="submit()"
  >
    <div>
      <v-card flat>
        <v-card-text>
          <div v-if="loading" class="text-center">
            <v-progress-circular
                color="primary"
                indeterminate
            ></v-progress-circular>
          </div>
          <div class="w-full text-center">
            <div v-if="showDateWarning">
              <v-divider></v-divider>
              <span>
                <p style="margin-top: 16px">
                  <v-icon color="info">mdi-information-variant</v-icon>
                  Training event has not been completed yet. Certificates can be generated on or after
                  <b>{{ programEndDate }}</b>.
                </p>
              </span>
              <v-divider></v-divider>
            </div>
            <div v-if="emailMissing">
              <v-divider></v-divider>
              <span>
                <p style="margin-top: 16px">
                  <v-icon color="info">mdi-information-variant</v-icon>
                  Add the trainer email before completing the course so the certificate can be delivered.
                </p>
              </span>
              <v-divider></v-divider>
            </div>
          </div>
          <div class="flex my-4 mb-4 pb-3">
            <div class="w-full md:w-1/2 px-3 mb-6 required">
              <label
                  class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              "
              >Course Completion Date</label
              >
            </div>
            <div class="w-1/3 h-12">
              <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="formValues.courseCompletionDate"
                  min-width="auto"
                  offset-y
                  transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="formValues.courseCompletionDate"
                      label="course completion date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                    v-model="formValues.courseCompletionDate"
                    :max="date"
                    no-title
                    scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="menu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                      color="primary"
                      text
                      @click="$refs.menu.save(formValues.courseCompletionDate)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </div>
          <div class="flex my-4 mb-4 pb-3">
            <div class="w-full md:w-1/2 px-3 mb-6 required">
              <label
                  class="
                block
                uppercase
                tracking-wide
                text-gray-700 text-xs
                font-bold
                mb-2
              "
              >Certificate Reason</label
              >
            </div>

            <div class="w-1/3 h-12 required">
              <validation-provider v-slot="{ errors }" rules="required">
                <v-select
                    v-model="formValues.certificate_reason"
                    :elevation="1"
                    :items="CERTIFICATE_REASON"
                    :loading="certReasonLoading"
                    dense
                    hint="Select Certificate Reason"
                    item-text="name"
                    return-object
                    solo
                >
                </v-select>

                <span class="text-gray-600 text-xs italic text-red-700">{{
                    errors[0]
                  }}</span>
              </validation-provider>
              <br/>
            </div>

            <div class="d-flex flex-row-reverse mt-4">
              <div class="w-1/3 h-12 mt-2">
                <div class="d-flex flex-row-reverse space-around mt-2">
                  <v-btn
                      class="teal white--text ml-3 mt-2"
                      color="primary"
                      :disabled="disableButtons"
                      depressed
                      small
                      @click="updateCourse()"
                  >
                    Complete Course
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </ValidationObserver>
</template>

<script>
import {extend, ValidationObserver, ValidationProvider} from "vee-validate";
import {required} from "vee-validate/dist/rules";
import {getCertReason} from "@/utils/helper";
import ethiopic from "ethiopic-calendar";

extend("required", {
  ...required,
  message: "This field is required",
});

export default {
  props: {
    trainerCourseDetail: Object,
    trainerEmail: String,
  },
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      loading: false,
      formValues: {
        trainerId: null,
        courseId: null,
        programId: null,
        certificateReasonId: null,
        courseCompletionDate: null,
        certificateGenerated: null,
        certificateApprovalStatus: null,
        certificate_reason: null,
        isCategory1: null
      },
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
      menu: false,
      modal: false,
      CERTIFICATE_REASON: [],
      certReasonLoading: false,
      imageSrc: "",
      isCategory1: false,
      etDate: null,
      showDateWarning: false,
      disableButtons: false,
      programEndDate: null
    };
  },
  async created() {
    this.etDate = this.convertGE(this.date);
    await this.initLookups();
    if (this.trainerCourseDetail != null) {
      this.isCategory1 = this.trainerCourseDetail.program && (this.trainerCourseDetail.program?.courseTitleCat2 != null && this.trainerCourseDetail.program?.courseTitleCat2 != "") ? false : true
      this.formValues = this.trainerCourseDetail;
      this.formValues.courseCompletionDate = this.date;
      this.setCompletionWarning(this.trainerCourseDetail.program?.endDate);
    }
  },
  computed: {
    emailMissing() {
      return !this.trainerEmail || this.trainerEmail.trim() === "";
    }
  },
  methods: {
    convertGE(val) {
      if (!val) return val;
      const [year, month, day] = val.split("-").map(Number);
      if (this.pickerType === "year") {
        let etDate = ethiopic.ge(year, month || 6, day || 1); // so it will be the same year in both directions
        return etDate.year.toString().padStart(4, "0");
      } else {
        let etDate = ethiopic.ge(year, month || 1, day || 1);
        return (
            etDate.year.toString().padStart(4, "0") +
            "-" +
            etDate.month.toString().padStart(2, "0") +
            "-" +
            etDate.day.toString().padStart(2, "0")
        );
      }
    },
    async initLookups() {
      this.certReasonLoading = true;
      const certResp = await getCertReason();
      this.CERTIFICATE_REASON = certResp?.rows.filter((data) => {
        return data.isActive === true;
      });
      this.certReasonLoading = false;
    },
    async updateCourse() {
      if (!(await this.$refs.observer.validate())) return;
      if (this.disableButtons) {
        this.$toast.error("Certificate generation is not allowed yet.");
        return;
      }
      this.$refs.observer.validate();
      this.loading = true;
      const value = {
        ...this.formValues
      }
      value.certificateReasonId = value.certificate_reason.id;
      if (this.isCategory1) {
        value.isCategory1 = true;
      } else {
        value.isCategory1 = false;
      }
      value.certificateGenerated = true;
      value.certificateApprovalStatus = "PENDING";
      this.$emit("completeCourse", value)
    },
    setCompletionWarning(endDate) {
      if (!endDate) return;
      const programEndDate = new Date(endDate);
      const today = new Date();
      programEndDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(programEndDate.getTime()) && programEndDate > today) {
        this.showDateWarning = true;
        this.disableButtons = true;
        this.programEndDate = programEndDate.toISOString().slice(0, 10);
      }
    }
  },
};
</script>
